// Hand-tuned icon set for the Workspace Agent console.
// All glyphs share: 16x16 viewbox, 1.5 stroke, round caps/joins, currentColor.
// Linear-tight geometry — calmer than lucide defaults, single optical weight.
import { cn } from '@/lib/utils'

interface IconProps {
    size?: number
    className?: string
    strokeWidth?: number
    'aria-hidden'?: boolean
}

function baseProps({ size = 16, strokeWidth = 1.5, className, ...rest }: IconProps) {
    return {
        width: size,
        height: size,
        viewBox: '0 0 16 16',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
        className: cn('shrink-0', className),
        'aria-hidden': true,
        ...rest,
    }
}

export function PlusIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M8 3.5v9M3.5 8h9' />
        </svg>
    )
}

export function SurveyIcon(props: IconProps) {
    // Rounded card with answer lines + a marked option — calm, no fill clutter.
    return (
        <svg {...baseProps(props)}>
            <rect x='2.75' y='2.5' width='10.5' height='11' rx='2.25' />
            <path d='M5.25 6.25h5.5M5.25 8.5h5.5' />
            <circle cx='5.85' cy='10.85' r='0.7' fill='currentColor' stroke='none' />
            <path d='M7.1 10.85h3.65' />
        </svg>
    )
}

export function PaperclipIcon(props: IconProps) {
    // Single sweeping clip — clean read, few joins.
    return (
        <svg {...baseProps(props)}>
            <path d='M11.6 7.4 7.6 11.4a2.4 2.4 0 1 1-3.4-3.4l5.1-5.1a1.6 1.6 0 1 1 2.26 2.26L6.6 10.1a0.8 0.8 0 0 1-1.13-1.13l4.2-4.2' />
        </svg>
    )
}

export function ArrowUpIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M8 13V3.5M4.25 7.25 8 3.5l3.75 3.75' />
        </svg>
    )
}

export function CheckIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M3.25 8.5 6.1 11.35 12.75 4.75' />
        </svg>
    )
}

export function AlertIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <circle cx='8' cy='8' r='5.5' />
            <path d='M8 5.25v3.25' />
            <circle cx='8' cy='10.6' r='0.6' fill='currentColor' stroke='none' />
        </svg>
    )
}

export function LockIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <rect x='3.5' y='7.25' width='9' height='6' rx='1.5' />
            <path d='M5.5 7.25V5.5a2.5 2.5 0 0 1 5 0v1.75' />
        </svg>
    )
}

export function SpinnerIcon({ className, size = 16, strokeWidth = 1.5 }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox='0 0 16 16'
            fill='none'
            aria-hidden='true'
            className={cn('agent-spinner shrink-0', className)}
        >
            <circle
                cx='8'
                cy='8'
                r='5.75'
                stroke='currentColor'
                strokeWidth={strokeWidth}
                className='agent-spinner__track'
            />
        </svg>
    )
}

export function MenuIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M3 5h10M3 8h10M3 11h10' />
        </svg>
    )
}

export function CloseIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='m4 4 8 8M12 4l-8 8' />
        </svg>
    )
}

export function ArrowRightIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M3.5 8h9M8.75 4.25 12.5 8l-3.75 3.75' />
        </svg>
    )
}

export function SettingsIcon(props: IconProps) {
    // 6-tooth gear — calmer than lucide's 8-tooth default.
    return (
        <svg {...baseProps(props)}>
            <circle cx='8' cy='8' r='2' />
            <path d='M8 1.75v1.6M8 12.65v1.6M2.85 5l1.4.8M11.75 10.2l1.4.8M2.85 11l1.4-.8M11.75 5.8l1.4-.8' />
        </svg>
    )
}

export function ChevronDownIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='m4 6 4 4 4-4' />
        </svg>
    )
}

export function ReturnKeyIcon(props: IconProps) {
    // Compact "↵" used inside the composer kbd chip for the send-shortcut hint.
    return (
        <svg {...baseProps(props)}>
            <path d='M12 4.5v3a2 2 0 0 1-2 2H4M6.5 7 4 9.5 6.5 12' />
        </svg>
    )
}

export function StopIcon({ size = 16, className }: IconProps) {
    // Filled rounded square — the universal "stop generation" glyph.
    return (
        <svg
            width={size}
            height={size}
            viewBox='0 0 16 16'
            aria-hidden='true'
            className={cn('shrink-0', className)}
        >
            <rect
                x='4'
                y='4'
                width='8'
                height='8'
                rx='1.5'
                fill='currentColor'
            />
        </svg>
    )
}

export function CopyIcon(props: IconProps) {
    // Two overlapping rounded rects — calmer than the lucide default.
    return (
        <svg {...baseProps(props)}>
            <rect x='5.5' y='5.5' width='8' height='8' rx='1.5' />
            <path d='M10.5 5.5V4A1.5 1.5 0 0 0 9 2.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5' />
        </svg>
    )
}

export function ExitIcon(props: IconProps) {
    // Door + arrow pointing out — communicates "exit / leave this mode".
    return (
        <svg {...baseProps(props)}>
            <path d='M9.25 3.25H4a1.25 1.25 0 0 0-1.25 1.25v7A1.25 1.25 0 0 0 4 12.75h5.25' />
            <path d='M7.25 8h6.25M11 5.5 13.5 8 11 10.5' />
        </svg>
    )
}

export function SparkIcon(props: IconProps) {
    // Four-point spark — used for "Suggestion" chips on the empty state.
    return (
        <svg {...baseProps(props)}>
            <path d='M8 2.5v3M8 10.5v3M2.5 8h3M10.5 8h3' />
            <path d='M4.6 4.6 6 6M10 10l1.4 1.4M4.6 11.4 6 10M10 6l1.4-1.4' />
        </svg>
    )
}

export function SearchIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <circle cx='7.25' cy='7.25' r='3.75' />
            <path d='m10 10 3 3' />
        </svg>
    )
}

export function PinIcon({ filled, ...rest }: IconProps & { filled?: boolean }) {
    // Outline pin with optional fill so the pinned state reads at a glance.
    const props = baseProps(rest)
    return (
        <svg {...props}>
            <path
                d='M9.5 2.5 13.5 6.5 11.25 7 9.75 8.5 9.75 11 6.5 7.75 4 11.5l-.5-.5L7.25 7.5 9 6 9.5 2.5z'
                fill={filled ? 'currentColor' : 'none'}
            />
        </svg>
    )
}

export function TrashIcon(props: IconProps) {
    return (
        <svg {...baseProps(props)}>
            <path d='M3.5 4.5h9M6 4.5V3.25A1 1 0 0 1 7 2.25h2a1 1 0 0 1 1 1V4.5' />
            <path d='M4.75 4.5 5.5 13a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.75-8.5' />
            <path d='M7 7v4M9 7v4' />
        </svg>
    )
}

export function RefreshIcon(props: IconProps) {
    // Single curved arrow loop — used for the "Regenerate response" action.
    return (
        <svg {...baseProps(props)}>
            <path d='M13.25 8a5.25 5.25 0 1 1-1.55-3.71' />
            <path d='M13.25 2.5v3.5h-3.5' />
        </svg>
    )
}

export function EditIcon(props: IconProps) {
    // Pencil with a soft tip — used for "rename" in the sidebar row.
    return (
        <svg {...baseProps(props)}>
            <path d='M10.5 2.75 13.25 5.5 5.75 13l-3 .25.25-3z' />
            <path d='M9.25 4 12 6.75' />
        </svg>
    )
}

export type { IconProps }
